import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  FiActivity,
  FiArrowLeft,
  FiBarChart2,
  FiCreditCard,
  FiDownload,
  FiFlag,
  FiGrid,
  FiInbox,
  FiMap,
  FiHelpCircle,
  FiHome,
  FiLifeBuoy,
  FiGift,
  FiPackage,
  FiSettings,
  FiStar,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { FaTelegram, FaYoutube } from "react-icons/fa";
import { useAuthStore } from "../../store/authStore";
import { useLanguage } from "../../contexts/LanguageContext";
import { useDismiss } from "../../hooks/useDismiss";
import { GAMEGUARD_URL } from "../../lib/constants";
import { SOCIAL_LINKS } from "../UI/SocialLinks";
import BrandMark from "./BrandMark";

/* Полный сайдбар ≥1280, rail 768–1279, drawer <768 (design-spec 03). */
const RAIL = "(min-width: 768px) and (max-width: 1279px)";

const Aside = styled.aside<{ $open: boolean }>`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: ${({ theme }) => theme.zIndex.fixed};
  width: ${({ theme }) => theme.layout.sidebar};
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.navigation};
  @media ${RAIL} {
    width: ${({ theme }) => theme.layout.rail};
  }
  @media (max-width: 767px) {
    width: min(300px, 86vw);
    transform: translateX(${({ $open }) => ($open ? "0" : "-100%")});
    visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
    box-shadow: ${({ $open }) => ($open ? "12px 0 40px #000a" : "none")};
    transition:
      transform ${({ theme }) => theme.motion.dialog} ${({ theme }) => theme.motion.ease},
      visibility 0s linear ${({ $open, theme }) => ($open ? "0s" : theme.motion.dialog)};
  }
`;
const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ theme }) => theme.layout.header};
  padding: 0 20px;
  @media ${RAIL} {
    justify-content: center;
    padding: 0;
    .brand-text {
      display: none;
    }
  }
  @media (max-width: 767px) {
    height: ${({ theme }) => theme.layout.headerMobile};
  }
`;
const CloseBtn = styled.button`
  display: none;
  place-items: center;
  width: 44px;
  height: 44px;
  margin-right: -10px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  &:hover {
    background: ${({ theme }) => theme.colors.raised};
    color: ${({ theme }) => theme.colors.text};
  }
  @media (max-width: 767px) {
    display: grid;
  }
`;
const Nav = styled.nav`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 12px;
  ul {
    list-style: none;
    display: grid;
    gap: 2px;
  }
  @media ${RAIL} {
    padding: 8px 12px;
  }
`;
const Item = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 15px;
  font-weight: 550;
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast};
  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.raised};
    color: ${({ theme }) => theme.colors.text};
  }
  &.active {
    background: ${({ theme }) => theme.colors.raised};
    color: ${({ theme }) => theme.colors.text};
    svg {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  @media ${RAIL} {
    justify-content: center;
    padding: 0;
    .label {
      display: none;
    }
  }
`;
const Bottom = styled.div`
  position: relative;
  padding: 8px 12px 16px;
`;
const HelpBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 0 12px;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 550;
  white-space: nowrap;
  text-align: left;
  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
  }
  &:hover,
  &[aria-expanded="true"] {
    background: ${({ theme }) => theme.colors.raised};
    color: ${({ theme }) => theme.colors.text};
  }
  @media ${RAIL} {
    justify-content: center;
    padding: 0;
    .label {
      display: none;
    }
  }
`;
const Menu = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: calc(100% - 4px);
  z-index: 2;
  display: grid;
  padding: 6px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.raised};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 16px 40px #0009;
  @media ${RAIL} {
    left: calc(100% - 4px);
    right: auto;
    bottom: 16px;
    width: 264px;
  }
  a {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 44px;
    padding: 0 10px;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    font-weight: 500;
    &:hover,
    &:focus-visible {
      background: ${({ theme }) => theme.colors.surfaceHover};
    }
    svg {
      width: 18px;
      height: 18px;
      color: ${({ theme }) => theme.colors.textSecondary};
    }
    small {
      display: block;
      color: ${({ theme }) => theme.colors.textMuted};
      font-size: 12px;
      line-height: 16px;
    }
  }
  hr {
    border: 0;
    height: 1px;
    margin: 4px 6px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: Props) {
  const { user } = useAuthStore();
  const { t } = useLanguage();
  const [help, setHelp] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLButtonElement>(null);
  const closeHelp = React.useCallback(() => setHelp(false), []);
  useDismiss(help, closeHelp, bottomRef, helpRef);
  const firstLink = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (isOpen && window.matchMedia("(max-width: 767px)").matches)
      firstLink.current?.focus();
  }, [isOpen]);

  const { pathname } = useLocation();
  const adminMode = pathname.startsWith("/admin") && !!user?.isAdmin;
  const adminItems = [
    { path: "/admin/overview", icon: FiGrid, label: t("adm.overview") },
    { path: "/admin/users", icon: FiUsers, label: t("adm.users") },
    { path: "/admin/maps", icon: FiMap, label: t("adm.maps") },
    { path: "/admin/subscriptions", icon: FiStar, label: t("adm.subscriptions") },
    { path: "/admin/finance", icon: FiCreditCard, label: t("adm.finance") },
    { path: "/admin/moderation", icon: FiFlag, label: t("adm.moderation") },
    { path: "/admin/analytics", icon: FiActivity, label: t("adm.analytics") },
    { path: "/admin/settings", icon: FiSettings, label: t("adm.settings") },
    { path: "/admin/support", icon: FiInbox, label: t("adm.support") },
  ];
  const playerItems = [
    { path: "/", icon: FiHome, label: t("nav.home") },
    { path: "/profile", icon: FiUser, label: t("nav.profile") },
    { path: "/stats", icon: FiBarChart2, label: t("nav.stats") },
    { path: "/inventory", icon: FiPackage, label: t("nav.inventory") },
    { path: "/clans", icon: FiUsers, label: t("nav.clans") },
    { path: "/subscription", icon: FiStar, label: t("nav.subscription") },
    { path: "/referral", icon: FiGift, label: t("nav.referral") },
    { path: "/support", icon: FiLifeBuoy, label: t("nav.support") },
    ...(user?.isAdmin
      ? [{ path: "/admin/overview", icon: FiSettings, label: t("nav.admin") }]
      : []),
  ];
  const items = adminMode ? adminItems : playerItems;

  return (
    <Aside $open={isOpen} aria-label={t("common.main_nav")}>
      <Top>
        <BrandMark />
        <CloseBtn type="button" aria-label={t("common.close")} onClick={onClose}>
          <FiX size={22} aria-hidden="true" />
        </CloseBtn>
      </Top>
      <Nav aria-label={t("common.main_nav")}>
        <ul>
          {items.map((it, i) => (
            <li key={it.path}>
              <Item
                ref={i === 0 ? firstLink : undefined}
                to={it.path}
                end={it.path === "/"}
                title={it.label}
                aria-label={it.label}
                onClick={onClose}
              >
                <it.icon aria-hidden="true" />
                <span className="label">{it.label}</span>
              </Item>
            </li>
          ))}
        </ul>
      </Nav>
      <Bottom ref={bottomRef}>
        {adminMode ? (
          <Item to="/" end title={t("adm.platform")} aria-label={t("adm.platform")} onClick={onClose}>
            <FiArrowLeft aria-hidden="true" />
            <span className="label">{t("adm.platform")}</span>
          </Item>
        ) : (
          <>
        {help && (
          <Menu role="group" aria-label={t("nav.help")}>
            <a href={GAMEGUARD_URL} target="_blank" rel="noopener noreferrer">
              <FiDownload aria-hidden="true" />
              <span>
                {t("sidebar.anticheat")}
                <small>{t("sidebar.anticheat_hint")}</small>
              </span>
            </a>
            <hr />
            <a href={SOCIAL_LINKS.telegram} target="_blank" rel="noopener noreferrer">
              <FaTelegram aria-hidden="true" />
              {t("social.telegram")}
            </a>
            <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noopener noreferrer">
              <FaYoutube aria-hidden="true" />
              {t("social.youtube")}
            </a>
          </Menu>
        )}
        <HelpBtn
          ref={helpRef}
          type="button"
          aria-haspopup="true"
          aria-expanded={help}
          aria-label={t("nav.help")}
          title={t("nav.help")}
          onClick={() => setHelp((v) => !v)}
        >
          <FiHelpCircle aria-hidden="true" />
          <span className="label">{t("nav.help")}</span>
        </HelpBtn>
          </>
        )}
      </Bottom>
    </Aside>
  );
}
